import {
  STRIPE_PUBLIC_KEY,
  STRIPE_SEMIANNUAL_PRICE_ID,
  STRIPE_ANNUAL_PRICE_ID,
  STRIPE_SEMIANNUAL_PRODUCT_ID,
  STRIPE_ANNUAL_PRODUCT_ID,
  IS_DEV,
} from "@/config/env";

import { PLANS } from "@/config/stripe";
import { getPriceById, getProductById } from "@/services/stripe";

/**
 * Check if the Stripe configuration is valid
 * This is useful for verifying that the correct environment is being used
 */
export const checkStripeConfig = (): {
  isValid: boolean;
  environment: string;
  details: Record<string, any>;
} => {
  // Check if the public key is for test or live environment
  const isTestKey = STRIPE_PUBLIC_KEY.startsWith("pk_test_");
  const isLiveKey = STRIPE_PUBLIC_KEY.startsWith("pk_live_");
  const environment = isTestKey ? "test" : isLiveKey ? "live" : "unknown";

  // Check if price IDs exist
  const semiannualPrice = getPriceById(STRIPE_SEMIANNUAL_PRICE_ID);
  const annualPrice = getPriceById(STRIPE_ANNUAL_PRICE_ID);

  // Check if product IDs exist
  const semiannualProduct = getProductById(STRIPE_SEMIANNUAL_PRODUCT_ID);
  const annualProduct = getProductById(STRIPE_ANNUAL_PRODUCT_ID);

  // Check if the configuration is valid
  const isValid = Boolean(
    (isTestKey || isLiveKey) &&
      semiannualPrice &&
      annualPrice &&
      semiannualProduct &&
      annualProduct
  );

  // Collect details for logging
  const details = {
    publicKey: {
      value: STRIPE_PUBLIC_KEY.substring(0, 8) + "...",
      isTest: isTestKey,
      isLive: isLiveKey,
      isValid: isTestKey || isLiveKey,
    },
    semiannualPrice: {
      id: STRIPE_SEMIANNUAL_PRICE_ID,
      exists: Boolean(semiannualPrice),
      amount: semiannualPrice?.unitAmount
        ? `${semiannualPrice.unitAmount / 100} ${semiannualPrice.currency}`
        : "N/A",
    },
    annualPrice: {
      id: STRIPE_ANNUAL_PRICE_ID,
      exists: Boolean(annualPrice),
      amount: annualPrice?.unitAmount
        ? `${annualPrice.unitAmount / 100} ${annualPrice.currency}`
        : "N/A",
    },
    semiannualProduct: {
      id: STRIPE_SEMIANNUAL_PRODUCT_ID,
      exists: Boolean(semiannualProduct),
      name: semiannualProduct?.name || "N/A",
    },
    annualProduct: {
      id: STRIPE_ANNUAL_PRODUCT_ID,
      exists: Boolean(annualProduct),
      name: annualProduct?.name || "N/A",
    },
    plans: {
      semiannual: {
        name: PLANS.SEMIANNUAL.name,
        price: PLANS.SEMIANNUAL.price,
        formattedPrice: PLANS.SEMIANNUAL.formattedPrice,
        billingInterval: PLANS.SEMIANNUAL.billingInterval,
      },
      annual: {
        name: PLANS.YEARLY.name,
        price: PLANS.YEARLY.price,
        formattedPrice: PLANS.YEARLY.formattedPrice,
        billingInterval: PLANS.YEARLY.billingInterval,
      },
    },
  };

  return {
    isValid,
    environment,
    details,
  };
};

/**
 * Log the Stripe configuration to the console
 * This is useful for debugging
 */
export const logStripeConfig = (): void => {
  if (!IS_DEV) return; // Only log in development

  const config = checkStripeConfig();

  // Stripe configuration details are available in config object
  // but not logged to console
};

// Automatically log the configuration in development
if (IS_DEV) {
  // Wait for the DOM to load before logging
  if (typeof window !== "undefined") {
    window.addEventListener("DOMContentLoaded", () => {
      setTimeout(logStripeConfig, 1000); // Delay to ensure Stripe service is initialized
    });
  }
}
